import React, { useRef, useEffect } from 'react';
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  PBRMaterial,
  MeshBuilder,
  Texture,
  CubeTexture,
  HemisphericLight,
  PointLight,
  ShadowGenerator,
  Color3,
  VertexData,
  Plane,                              // <-- For mirrorPlane definition
} from '@babylonjs/core';

import '@babylonjs/loaders'; // For .env/.hdr/.glTF, etc.

// Import MirrorTexture
import { MirrorTexture } from '@babylonjs/core/Materials/Textures/mirrorTexture';

import { CSG } from '@babylonjs/core/Meshes/csg';

const WhiteTileSpherePreview = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const engine = new Engine(canvas, true);
    const scene = new Scene(engine);

    // ------------------------------------------------
    // 1) Camera
    // ------------------------------------------------
    const camera = new ArcRotateCamera('camera', Math.PI / 4, Math.PI / 3, 6, Vector3.Zero(), scene);
    camera.wheelPrecision = 50; 
    camera.attachControl(canvas, true);

    // ------------------------------------------------
    // 2) Lights
    // ------------------------------------------------
    const downlightPositions = [
      new Vector3(0.5, 1, 0.5),
      new Vector3(0.5, 1, -0.5),
      new Vector3(-0.5, 1, 0.5),
      new Vector3(-0.5, 1, -0.5),
    ];

    downlightPositions.forEach((pos, i) => {
      const pointLight = new PointLight(`pointLight${i}`, pos, scene);
      pointLight.intensity = 1;
      pointLight.range = 2;
      pointLight.diffuse = new Color3(1.0, 0.95, 0.9);
    });

    // ------------------------------------------------
    // 3) Material for the "walls" (PBR)
    // ------------------------------------------------
    const cubeMat = new PBRMaterial('whiteCeramicPBR', scene);

    const albedoTex = new Texture('/textures/walls/TilesMosaicYubi003_COL_8K.png', scene);
    cubeMat.albedoTexture = albedoTex;

    const normalTex = new Texture('/textures/walls/TilesMosaicYubi003_NRM_8K.png', scene);
    normalTex.invertNormalMapY = false;
    cubeMat.bumpTexture = normalTex;

    const glossTex = new Texture('/textures/walls/TilesMosaicYubi003_GLOSS_8K.png', scene);
    cubeMat.specularGlossinessTexture = glossTex;

    const reflectionTex = new Texture('/textures/walls/TilesMosaicYubi003_REFL_8K.png', scene);
    cubeMat.reflectivityTexture = reflectionTex;

    const aoTex = new Texture('/textures/walls/TilesMosaicYubi003_AO_8K.png', scene);
    cubeMat.ambientTexture = aoTex;

    cubeMat.useSpecularOverAlpha = true;
    cubeMat.useAlphaFromAlbedoTexture = false;
    cubeMat.roughness = 0.1;
    cubeMat.metallic = 0.0;

    // -------------------------------------------------------
    // 4) Hollow "Cup" Box: 2m x 2m x 2m, 0.2m thick, no top
    // -------------------------------------------------------
    const outerBox = MeshBuilder.CreateBox('outerBox', { width: 2, height: 2, depth: 2 }, scene);
    const innerBox = MeshBuilder.CreateBox('innerBox', { width: 1.6, height: 2, depth: 1.6 }, scene);
    innerBox.position.y = 0.2; 

    const outerCSG = CSG.FromMesh(outerBox);
    const innerCSG = CSG.FromMesh(innerBox);
    const hollowCubeCSG = outerCSG.subtract(innerCSG);

    const hollowCube = hollowCubeCSG.toMesh('hollowCube', cubeMat, scene);

    outerBox.dispose();
    innerBox.dispose();

    // -----------------------------
    // 5) Recalculate UVs
    // -----------------------------
    hollowCube.computeWorldMatrix(true);
    const vData = VertexData.ExtractFromMesh(hollowCube);
    const positions = vData.positions;
    const normals   = vData.normals;
    const newUVs    = [];

    for (let i = 0; i < positions.length; i += 3) {
      const x  = positions[i + 0];
      const y  = positions[i + 1];
      const z  = positions[i + 2];

      const nx = normals[i + 0];
      const ny = normals[i + 1];
      const nz = normals[i + 2];

      const absNx = Math.abs(nx);
      const absNy = Math.abs(ny);
      const absNz = Math.abs(nz);

      let u = 0, v = 0;

      if (absNx >= absNy && absNx >= absNz) {
        // ±X wall => (z, y)
        u = z;
        v = y;
      } else if (absNz >= absNx && absNz >= absNy) {
        // ±Z wall => (x, y)
        u = x;
        v = y;
      } else {
        // Floor => (x, z)
        u = x;
        v = z;
      }

      const scale = 0.5; // Adjust the tiling
      newUVs.push(u * scale, v * scale);
    }
    vData.uvs = newUVs;
    vData.applyToMesh(hollowCube);

    // ------------------------------------------------
    // 6) Create the mirror at (1.8, 0, 1.8)
    // ------------------------------------------------
    // 6.1) Create a MirrorTexture
    const mirrorTexture = new MirrorTexture('mirrorTex', 1024, scene, true);
    // By default, a plane in Babylon faces +Z. We'll rotate it, but let's set up the mirror plane object.
    // If your mirror is effectively facing -Z, you'd do mirrorPlane = new Plane(0,0,-1,0), etc.

    // 6.2) Create the plane mesh
    const mirrorPlaneMesh = MeshBuilder.CreatePlane('mirrorPlane', {
      width: 0.5,
      height: 0.5,
    }, scene);

    // Position it at (1.8, 0, 1.8)
    mirrorPlaneMesh.position.set(0.6, 0, 0.6);

    // Make it face (0,0,0)
    mirrorPlaneMesh.lookAt(Vector3.Zero()); 
    // Alternatively: mirrorPlaneMesh.lookAt(new Vector3(0,0,0), 0, 1, 0)

    // 6.3) Create a PBR material that uses the mirrorTexture
    const mirrorMat = new PBRMaterial('mirrorMat', scene);
    mirrorMat.metallic = 1.0;        // fully metallic
    mirrorMat.roughness = 0.0;       // zero roughness => perfect mirror
    mirrorMat.backFaceCulling = false;

    // Attach the mirrorTexture to reflection
    mirrorMat.reflectionTexture = mirrorTexture;
    mirrorMat.reflectionTexture.level = 1.0; // reflection intensity

    mirrorPlaneMesh.material = mirrorMat;

    // 6.4) Let the MirrorTexture know what plane it’s mirrored about
    // If your plane is facing the origin, the normal is from mirror to origin.
    // But let's do a simpler approach: Because we used .lookAt(...),
    // we can set the mirrorTexture.mirrorPlane from position+normal.
    // We'll dynamically compute that each frame. 
    // For a more direct approach, you could do your own plane logic:
    //   plane normal = mirrorPlaneMesh.getNormal();
    //   plane point  = mirrorPlaneMesh.position;
    // Then create a `Plane.FromPositionAndNormal(point, normal)`

    scene.onBeforeRenderObservable.add(() => {
      // Rebuild mirrorPlane every frame in case the mesh is moved/rotated
      const normal = mirrorPlaneMesh.getFacetNormal(0); 
      // or you could do something like mirrorPlaneMesh.getDirection(new Vector3(0,0,1))
      // or normal = mirrorPlaneMesh.forward depending on your desired axis

      // The plane is "plane(normal.x, normal.y, normal.z, d)"
      // where d = -Vector3.Dot(normal, planePosition)
      // planePosition = mirrorPlaneMesh.position
      const planePosition = mirrorPlaneMesh.position;
      const d = -Vector3.Dot(normal, planePosition);
      mirrorTexture.mirrorPlane = new Plane(normal.x, normal.y, normal.z, d);

      // Exclude the mirror from reflecting itself
      const allButMirror = scene.meshes.filter(m => m !== mirrorPlaneMesh);
      mirrorTexture.renderList = allButMirror;
    });

    // ------------------------------------------------
    // 7) Shadows (if any)
    // ------------------------------------------------
    scene.lights.forEach((light) => {
      if (light.getShadowGenerator()) {
        light.getShadowGenerator().addShadowCaster(hollowCube);
      }
    });

    // ------------------------------------------------
    // 8) Render loop
    // ------------------------------------------------
    engine.runRenderLoop(() => {
      scene.render();
    });

    // Cleanup on unmount
    return () => {
      scene.dispose();
      engine.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '900px', display: 'block' }}
    />
  );
};

export default WhiteTileSpherePreview;
